import React, {createContext, useContext, useEffect, useState} from 'react';
import {ClickAwayListener} from '@mui/base';
import clsx from 'clsx';
import useHorizontalScroll from '@di-shared/utils/useHorizontalScroll';
import {UNIT} from '@di-shared/const';

interface ChargingHabitGraphProps {
  guideLines: number[];
  children: React.ReactNode;
  showCautionZone: boolean;
  cautionLevel: number;
}

const DEFAULT_CAUTION_LEVEL = 10;
const CautionLevelContext = createContext<number>(DEFAULT_CAUTION_LEVEL);

function ChargingHabitGraph({guideLines, children, cautionLevel, showCautionZone}: ChargingHabitGraphProps) {
  const validGuideLines: number[] = [...new Set(guideLines.map((percent) => (percent > 100 ? 100 : percent < 0 ? 0 : percent)))];
  const {scrollRef, isScrollable, isAtLeft, isAtRight, scrollToEnd} = useHorizontalScroll<HTMLDivElement>();
  const [isTouching, setIsTouching] = useState<boolean>(false);
  useEffect(() => {
    scrollToEnd();
  }, []);
  return (
    <div
      className={clsx('flexible_scroll_graph', {'is-active': isTouching})}
      onTouchStart={() => setIsTouching(true)}
      onTouchEnd={() => setIsTouching(false)}
    >
      <div
        className="bar_graph_scroll"
        ref={scrollRef}
      >
        <div className="bar_graph">
          <CautionLevelContext.Provider value={cautionLevel}>{children}</CautionLevelContext.Provider>
        </div>
      </div>
      {validGuideLines.map((percent) => (
        <div
          className="bar_graph_average"
          style={{top: 161 - percent}}
          key={percent}
        >
          <span className="average_line" />
          <span className="average_text_box">
            <span className="average_value">
              {Math.round(percent)}
              {UNIT.PERCENT}
            </span>
          </span>
        </div>
      ))}
      {showCautionZone && (
        <div
          className="caution_area"
          style={{height: cautionLevel + 4}}
        >
          <div
            className="caution_zone"
            style={{height: cautionLevel + 4}}
          />
        </div>
      )}

      {isScrollable && (
        <div className="arrow_area">
          {isAtLeft || <span className="ic ic_arrow_double prev_arrow" />}
          {isAtRight || <span className="ic ic_arrow_double next_arrow" />}
        </div>
      )}
    </div>
  );
}

interface BarGraphItemProps {
  start: number;
  end: number;
  title: string;
  tooltipText: string;
}

function BarGraphItem({start, end, title, tooltipText}: BarGraphItemProps) {
  const cautionLevel = useContext(CautionLevelContext);
  const height = end - start;
  const [showTooltip, setShowTooltip] = React.useState(false);
  return (
    <div className={`bar_graph_item ${showTooltip && 'is-open'}`}>
      <span
        className="speech_bubble"
        style={{whiteSpace: 'pre-wrap', top: `${-18 - start}px`}}
      >
        {tooltipText}
      </span>
      <ClickAwayListener
        touchEvent="onTouchStart"
        onClickAway={() => setShowTooltip(false)}
      >
        <span
          className="graph_bar"
          style={{height, transform: `translateY(${-start - 3}px)`}}
          data-height={`${height}px`}
          onClick={() => setShowTooltip(true)}
        >
          <span
            className="graph_bar_notice"
            style={{transform: `translateY(${-18 + start}px)`, height: cautionLevel + 4}}
          />
        </span>
      </ClickAwayListener>

      <span className="graph_title">{title}</span>
    </div>
  );
}

ChargingHabitGraph.Item = BarGraphItem;
export default ChargingHabitGraph;
